/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { createSelector } from 'reselect';

import {
  INFLUENCER,
  PHOTOGRAPHER,
  MODEL,
  HEAD_STYLIST,
  CLOTHING_STYLIST,
} from 'actions/signup';

const INFLUENCER_INPUTS = ['talent_portfolio_1'];

const PHOTOGRAPHER_INPUTS = [
  'talent_portfolio_1',
  'talent_portfolio_2',
  'talent_portfolio_3',
];

const MODEL_INPUTS = [
  'talent_polaroid_face',
  'talent_portfolio_1',
  'talent_portfolio_2',
];

const HEAD_STYLIST_INPUTS = [
  'talent_portfolio_1',
  'talent_portfolio_2',
  'talent_portfolio_3',
];

const CLOTHING_STYLIST_INPUTS = [
  'talent_portfolio_1',
  'talent_portfolio_2',
  'talent_portfolio_3',
];

const getUser = state => state.user;
const getUserType = state => state.signup.type;
const getPortfolioForm = state => state.profileForm;

export const makeCreatePortfolioForm = () =>
  createSelector(
    [getUser, getUserType, getPortfolioForm],
    (user, userType, portfolioForm) => {
      const fieldsets = portfolioForm.fieldsets.map(({ legend, inputs }) => ({
        legend,
        inputs: inputs
          .map(input =>
            typeof user[input.name] !== 'undefined'
              ? { ...input, value: user[input.name] }
              : input
          )
          .map(input => {
            if (input.type === 'image') {
              const image =
                user.roles &&
                user.roles[userType] &&
                user.roles[userType].pictures
                  ? user.roles[userType].pictures.find(
                      picture => picture.index === input.index
                    )
                  : null;

              return {
                ...input,
                preview: image ? image.thumbnail_url : input.preview,
                value: image ? image.cloudinary_id : '',
              };
            }

            return { ...input };
          }),
      }));

      switch (userType) {
        case INFLUENCER:
          return {
            ...portfolioForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => INFLUENCER_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  user.roles &&
                  user.roles[user.type] &&
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case PHOTOGRAPHER:
          return {
            ...portfolioForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => PHOTOGRAPHER_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  user.roles &&
                  user.roles[user.type] &&
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case MODEL:
          return {
            ...portfolioForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => MODEL_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  user.roles &&
                  user.roles[user.type] &&
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case HEAD_STYLIST:
          return {
            ...portfolioForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => HEAD_STYLIST_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  user.roles &&
                  user.roles[user.type] &&
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case CLOTHING_STYLIST:
          return {
            ...portfolioForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(
                  input => CLOTHING_STYLIST_INPUTS.indexOf(input.name) !== -1
                )
                .map(input =>
                  user.roles &&
                  user.roles[user.type] &&
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        default:
          return {};
      }
    }
  );
